import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Field } from './field.model';
import { FormButton } from './formButton.model';
import { FunctionsAvailable, ItemType, ServiceRequest } from './request.model';
import { AsociacionesService, AsociadosService, EjerciciosService, CargosService, HistoricoService, TipoAsociacionesService } from '../../../api';
import { AlertService } from '../alert/alert.service';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';

export enum ServicesDataType {
  ASOCIACIONES = 'asociaciones',
  ASOCIADOS = 'asociados',
  EJERCICIOS = 'ejercicios',
  CARGOS = 'cargos',
  TIPOASOCIACIONES = 'tipoAsociaciones'
}

export interface EventSelectForm {
  id: any,
  key: string,
}

@Component({
  selector: 'app-formulario',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './formulario.component.html',
  styleUrl: './formulario.component.scss'
})
export class FormularioComponent {
  Number = Number;
  [key: string]: any;

  ItemType = ItemType;
  HTMLInputElement = HTMLInputElement;

  @Input()
  title!: string;
  @Input()
  buttons!: FormButton[];
  @Input()
  fields!: Field[];
  @Input()
  type!: ItemType;
  @Input()
  functionSelected!: FunctionsAvailable;

  notShowFields = ['password', 'confirmPassword'];

  formulario!: FormGroup;
  service!: ServiceRequest;

  loading: boolean = true;

  asociaciones: any = [];
  cargos: any = [];
  ejercicios: any = [];
  tipoAsociaciones: any = [];

  private servicesMap = {
    [ItemType.ASOCIACIONES]: this.asociacionesService,
    [ItemType.ASOCIADOS]: this.asociadosService,
    [ItemType.EJERCICIOS]: this.ejerciciosService,
    [ItemType.CARGOS]: this.cargosService,
    [ItemType.TIPOASOCIACIONES]: this.tipoAsociacionService,
    [ItemType.HISTORICO]: this.historicoService,
    [ItemType.POSTREQUESTASOCIADO]: this.asociadosService,
    [ItemType.CARGOASOCIADOASOCIACION]: this.cargosService,
    [ItemType.HISTORICOASOCIADO] : this.asociadosService
  };

  // Wave 2.2: antes este mapa lanzaba, al instanciar CUALQUIER formulario, las cinco peticiones (incluida la lista completa de
  // ~19.000 asociados, que ningún formulario usa). Ahora cada lista es una función perezosa: solo se pide la que el formulario
  // necesita y una vez por instancia. No hay lista de asociados: los selectores de asociados buscan bajo demanda
  // (app-asociado-buscador).
  private servicesDataMap: { [key in Exclude<ServicesDataType, ServicesDataType.ASOCIADOS>]: () => Promise<any> } = {
    [ServicesDataType.ASOCIACIONES]: () => firstValueFrom(this.asociacionesService.asociacionesGet()),
    [ServicesDataType.EJERCICIOS]: () => firstValueFrom(this.ejerciciosService.ejerciciosGet()),
    [ServicesDataType.CARGOS]: () => firstValueFrom(this.cargosService.cargosGet()),
    [ServicesDataType.TIPOASOCIACIONES]: () => firstValueFrom(this.tipoAsociacionService.tipoasociacionesGet()),
  };
  private cacheDatos = new Map<string, Promise<any>>();

  constructor(
    public asociacionesService: AsociacionesService,
    public asociadosService: AsociadosService,
    public ejerciciosService: EjerciciosService,
    public cargosService: CargosService,
    public tipoAsociacionService: TipoAsociacionesService,
    public historicoService: HistoricoService,
    public alertService: AlertService
  ) { }

  ngOnChanges() {
    this.loadFormData();
  }

  async loadFormData() {
    this.loading = true;
    await Promise.all([this.loadFields(), this.loadService()]);
    this.loading = false;
  }

  loadDataServer(item: ServicesDataType) {
    return new Promise(async resolve => {
      const cargador = (this.servicesDataMap as { [key: string]: (() => Promise<any>) | undefined })[item];
      if (cargador) {
        if (!this.cacheDatos.has(item)) this.cacheDatos.set(item, cargador());
        try {
          const data = await this.cacheDatos.get(item);
          if (data) {
            this[item] = data[item];
          }
        } catch {
          // Sin la lista el formulario sigue disponible (el selector queda vacio) en lugar de quedarse cargando para siempre
          this.cacheDatos.delete(item);
          this.alertService.danger('No se han podido cargar algunos datos del formulario.', 5000);
        }
      }
      resolve(true);
    })
  }

  loadFields() {
    return new Promise(async resolve => {
      // Las listas que necesita el formulario se piden EN PARALELO (antes ya estaban en vuelo desde el constructor)
      if(this.type === ItemType.POSTREQUESTASOCIADO || this.type === ItemType.HISTORICO) {
        await Promise.all([
          this.loadDataServer(ServicesDataType.ASOCIACIONES),
          this.loadDataServer(ServicesDataType.CARGOS),
          this.loadDataServer(ServicesDataType.EJERCICIOS)
        ]);
      }
      if (this.type === ItemType.ASOCIACIONES) {
        await this.loadDataServer(ServicesDataType.TIPOASOCIACIONES);
      }
      const formControls = this.fields.reduce(
        (acc, field) => ({ ...acc, [field.name]: new FormControl(field.value) }),
        {}
      );
      this.formulario = new FormGroup(formControls);
      resolve(true);
    })
  }

  loadService() {
    return new Promise(resolve => {
      this.service = this.servicesMap[this.type];
      resolve(true);
    })
  }

  onSubmit() {
  }

  changeSelected(event: EventSelectForm) {
    this.formulario.controls[event.key].setValue(Number(event.id.target.value));
    this.formulario.markAsTouched();
  }

  changeStaticSelected(event: EventSelectForm) {
    this.formulario.controls[event.key].setValue(event.id.target.value);
    this.formulario.markAsTouched();
  }

}
